<div class="flex flex-col px-4 py-4 rounded-b-xl gap-4 bg-white overflow-x-hidden">
  <!-- Summary Header -->
  <h1 class="text-2xl leading-snug font-medium">Zusammenfassung</h1>
  
  <!-- Festival Info -->
  <div class="flex flex-col gap-2 pb-4 border-b border-benzGrey-200">
    <h3 class="font-medium text-lg">{{ progressService.selectedFestival()?.title_public }}</h3>
    <div class="text-sm text-benzGrey-700">{{ progressService.selectedPersons() }} Personen</div>
    <app-progress-info [divider]="false"></app-progress-info>
  </div>

  <!-- Base Price Section -->
  <div class="flex flex-col gap-2 pb-4 border-b border-benzGrey-200">
    <div class="w-full flex justify-between gap-2 items-start">
      <div class="flex flex-col gap-2">
        @if(progressService.selectedPackage()) {
          <h4 class="font-medium">Inklusive Paket "{{ progressService.selectedPackage()?.name_public }}" pro Person:</h4>
          <ul class="text-sm text-benzGrey-700 list-disc list-inside">
            @for (product of progressService.selectedPackage()?.products; track product.id) {
              <li>{{ product.pivot!.amount }}x {{ product.name_public }}</li>
            }
          </ul>
        } @else {
          <h4 class="font-medium">Mindestverzehr pro Person:</h4>
          <ul class="text-sm text-benzGrey-700 list-disc list-inside">
            @for (product of selectedDaySlot()?.products; track product.id) {
              <li>{{ product.pivot!.amount }} {{ product.name_public }}</li>
            }
          </ul>
        }
      </div>
      <span class="font-semibold whitespace-nowrap">{{ basePriceSum() | currency:'EUR' }}</span>
    </div>
  </div>

  <!-- Additional Products Section -->
  @if (bookedProducts().length > 0) {
    <div class="flex flex-col gap-2 pb-4 border-b border-benzGrey-200">
      <h3 class="font-medium text-lg">Zusätzliche Tokens</h3>
      <div class="flex flex-col gap-1">
        @for (product of bookedProducts(); track product.id) {
          <div class="w-full flex justify-between text-sm">
            <span>{{ product.pivot!.amount }} {{ product.name_public }}</span>
            <span>{{ product.pivot!.amount * (+(product.price) + +(product.bediengeld || 0)) | currency:'EUR' }}</span>
          </div>
        }
      </div>
    </div>
  }

  <!-- Shipping Section -->
  <div class="flex flex-col gap-2 pb-4 border-b border-benzGrey-200">
    <h3 class="font-medium text-lg">Lieferung</h3>
    <div class="w-full flex justify-between text-sm">
      <span>{{ progressService.selectedShipping()?.buttonText }}</span>
      <span>{{ (progressService.selectedShipping()?.price || 0) | currency:'EUR' }}</span>
    </div>
  </div>

  <!-- Total -->
  <div class="w-full flex justify-between items-center py-2">
    <span class="text-xl font-semibold">Gesamt</span>
    <span class="text-2xl font-bold text-benzRed-700">{{ grandTotal() | currency:'EUR' }}</span>
  </div>
</div>

<!-- Checkout Form Section -->
<div class="flex flex-col my-4 gap-6 px-4">
  
  <!-- Pickup Time Section -->
  <div class="flex flex-col gap-3" #pickupTimeElem>
    <h2 class="text-2xl leading-snug font-medium">Ankunftszeit</h2>
    <p class="text-sm text-benzGrey-700">Bitte geben Sie Ihre Ankunftszeit an.</p>
    @if (!pickupTime && triedToSubmit) {
      <p class="text-benzRed-700 text-sm" role="alert" aria-live="assertive">
        Bitte wählen Sie eine Ankunftszeit aus.
      </p>
    }
    <label for="pickup-time-select" class="sr-only">Ankunftszeit auswählen</label>
    <select 
      id="pickup-time-select"
      [(ngModel)]="pickupTime" 
      [class.border-benzRed-700]="!pickupTime && triedToSubmit"
      class="bg-white border-2 border-benzGrey-900 invalid:font-light invalid:text-benzGrey-600 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700"
      aria-required="true"
      required>
      <option selected disabled hidden value="">Bitte wählen...</option>
      @for (arrivalOption of arrivalOptions(); track $index) {
        <option value="{{arrivalOption | date:'HH:mm'}}" class="text-benzGrey-900">
          {{arrivalOption | date:'HH:mm' }} Uhr
        </option>
      }
    </select>
    <p class="text-sm text-benzGrey-600" role="note">
      Ihre Reservierung wird nur 45 Minuten lang gehalten.
    </p>
  </div>

  <!-- Personal Information Section (NEW according to Figma) -->
  <div class="flex flex-col gap-3 pb-6 border-b border-benzGrey-200">
    <h2 class="text-2xl leading-snug font-medium">Rechnungsinformationen</h2>
    
    @if (authService.isAuthenticated()) {
      <!-- AUTHENTICATED USER: Address Selection from saved addresses -->
      <p class="text-sm text-benzGrey-700">Wählen Sie eine gespeicherte Adresse oder fügen Sie eine neue hinzu.</p>
      
      <app-address-list-selector
        [addresses]="savedBillingAddresses()"
        addressType="invoice"
        [selectedAddress]="selectedBillingAddress()"
        [showSameAsOption]="false"
        (addressSelected)="onBillingAddressSelected($event)"
        (addressCreated)="onBillingAddressAdded($event)">
      </app-address-list-selector>
      
      <!-- Additional fields for authenticated users (not in saved addresses) -->
      <div class="mt-4 flex flex-col gap-3 p-4 bg-benzGrey-50 rounded-lg">
        <p class="text-sm font-medium text-benzGrey-900">Zusätzliche Angaben</p>
        <form [formGroup]="personalInfoForm" class="flex flex-col gap-3">
          <!-- Ausweisnummer -->
          <app-inputerrors [control]="personalInfoForm.controls.id_number">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium">Ausweisnummer <span class="text-benzRed-700">*</span></span>
              <input 
                type="text" 
                formControlName="id_number" 
                placeholder="0123456789"
                [class.border-benzRed-700]="personalInfoForm.controls.id_number.touched && !personalInfoForm.controls.id_number.valid"
                class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
            </label>
          </app-inputerrors>

          <!-- Telefonnummer -->
          <app-inputerrors [control]="personalInfoForm.controls.phone">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium">Telefonnummer <span class="text-benzRed-700">*</span></span>
              <input 
                type="tel" 
                formControlName="phone" 
                placeholder="01234 567890"
                [class.border-benzRed-700]="personalInfoForm.controls.phone.touched && !personalInfoForm.controls.phone.valid"
                class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
            </label>
          </app-inputerrors>

          <!-- E-Mail -->
          <app-inputerrors [control]="personalInfoForm.controls.email">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium">E-Mail</span>
              <input 
                type="email" 
                formControlName="email" 
                placeholder="max@mustermann.de"
                [class.bg-benzGrey-100]="personalInfoForm.controls.email.disabled"
                [class.cursor-not-allowed]="personalInfoForm.controls.email.disabled"
                class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
            </label>
            @if (personalInfoForm.controls.email.disabled) {
              <p class="text-xs text-benzGrey-600 mt-1">
                Diese E-Mail-Adresse ist mit Ihrem Konto verknüpft.
              </p>
            }
          </app-inputerrors>
        </form>
      </div>
    } @else {
      <!-- GUEST USER: Manual form entry (original implementation) -->
      <p class="text-sm text-benzGrey-700">Füllen Sie Ihre Rechnungsinformationen aus.</p>
      
      <form [formGroup]="personalInfoForm" class="flex flex-col gap-3">
      <!-- Vorname -->
      <app-inputerrors [control]="personalInfoForm.controls.name">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Vorname <span class="text-benzRed-700">*</span></span>
          <input 
            type="text" 
            formControlName="name" 
            placeholder="Max"
            [class.border-benzRed-700]="personalInfoForm.controls.name.touched && !personalInfoForm.controls.name.valid"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
      </app-inputerrors>

      <!-- Nachname -->
      <app-inputerrors [control]="personalInfoForm.controls.surname">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Nachname <span class="text-benzRed-700">*</span></span>
          <input 
            type="text" 
            formControlName="surname" 
            placeholder="Mustermann"
            [class.border-benzRed-700]="personalInfoForm.controls.surname.touched && !personalInfoForm.controls.surname.valid"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
      </app-inputerrors>

      <!-- Ausweisnummer -->
      <app-inputerrors [control]="personalInfoForm.controls.id_number">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Ausweisnummer <span class="text-benzRed-700">*</span></span>
          <input 
            type="text" 
            formControlName="id_number" 
            placeholder="0123456789"
            [class.border-benzRed-700]="personalInfoForm.controls.id_number.touched && !personalInfoForm.controls.id_number.valid"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
      </app-inputerrors>

      <!-- Telefonnummer -->
      <app-inputerrors [control]="personalInfoForm.controls.phone">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Telefonnummer <span class="text-benzRed-700">*</span></span>
          <input 
            type="tel" 
            formControlName="phone" 
            placeholder="01234 567890"
            [class.border-benzRed-700]="personalInfoForm.controls.phone.touched && !personalInfoForm.controls.phone.valid"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
      </app-inputerrors>

      <!-- E-Mail -->
      <app-inputerrors [control]="personalInfoForm.controls.email">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">
            E-Mail 
            @if (!authService.isAuthenticated()) {
              <span class="text-benzRed-700">*</span>
            }
          </span>
          <input 
            type="email" 
            formControlName="email" 
            placeholder="max@mustermann.de"
            [class.border-benzRed-700]="personalInfoForm.controls.email.touched && !personalInfoForm.controls.email.valid"
            [class.bg-benzGrey-100]="personalInfoForm.controls.email.disabled"
            [class.cursor-not-allowed]="personalInfoForm.controls.email.disabled"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
        @if (authService.isAuthenticated() && personalInfoForm.controls.email.disabled) {
          <p class="text-xs text-benzGrey-600 mt-1">
            Diese E-Mail-Adresse ist mit Ihrem Konto verknüpft und kann hier nicht geändert werden.
          </p>
        }
      </app-inputerrors>

      <!-- Google Address Autocomplete -->
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium">Adresse suchen (zum Autofüllen)</span>
        <address-input (addressSelected)="onPersonalAddressSelected($event)"></address-input>
      </label>

      <!-- City -->
      <app-inputerrors [control]="personalInfoForm.controls.city">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Ort <span class="text-benzRed-700">*</span></span>
          <input 
            type="text" 
            formControlName="city" 
            placeholder="Stuttgart"
            [class.border-benzRed-700]="personalInfoForm.controls.city.touched && !personalInfoForm.controls.city.valid"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
      </app-inputerrors>

      <!-- Street -->
      <app-inputerrors [control]="personalInfoForm.controls.street">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Straße <span class="text-benzRed-700">*</span></span>
          <input 
            type="text" 
            formControlName="street" 
            placeholder="Kronprinzstr."
            [class.border-benzRed-700]="personalInfoForm.controls.street.touched && !personalInfoForm.controls.street.valid"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
      </app-inputerrors>

      <!-- Street Number -->
      <app-inputerrors [control]="personalInfoForm.controls.street_nr">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Hausnummer <span class="text-benzRed-700">*</span></span>
          <input 
            type="text" 
            formControlName="street_nr" 
            placeholder="24"
            [class.border-benzRed-700]="personalInfoForm.controls.street_nr.touched && !personalInfoForm.controls.street_nr.valid"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
      </app-inputerrors>

      <!-- Postal code -->
      <app-inputerrors [control]="personalInfoForm.controls.zipcode">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium">Postleitzahl <span class="text-benzRed-700">*</span></span>
          <input 
            type="text" 
            formControlName="zipcode" 
            placeholder="70173"
            [class.border-benzRed-700]="personalInfoForm.controls.zipcode.touched && !personalInfoForm.controls.zipcode.valid"
            class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        </label>
      </app-inputerrors>
      </form>
    }
  </div>

  <!-- Shipping Address Section -->
  @if (progressService.selectedShipping()?.requiresCountryCodeInDeliveryAdress !== false) {
    <div class="flex flex-col gap-3 pb-6 border-b border-benzGrey-200">
      <h2 class="text-2xl leading-snug font-medium">Lieferadresse</h2>
      
      @if (authService.isAuthenticated()) {
        <!-- AUTHENTICATED USER: Address Selection from saved addresses -->
        <app-address-list-selector
          [addresses]="savedShippingAddresses()"
          addressType="delivery"
          [selectedAddress]="selectedShippingAddress()"
          [showSameAsOption]="true"
          (addressSelected)="onShippingAddressSelected($event)"
          (sameAsSelected)="onUseSameAddressForShipping()"
          (addressCreated)="onShippingAddressAdded($event)">
        </app-address-list-selector>
      } @else {
        <!-- GUEST USER: Manual address entry -->
        <div class="flex flex-col gap-3">
          <!-- Option 1: Use same address -->
          <label 
            class="radio-button-container"
            [class.selected]="!useDifferentShippingAddress"
            (click)="useDifferentShippingAddress = false">
            <input 
              type="radio" 
              name="shipping-address-option" 
              [checked]="!useDifferentShippingAddress"
              (change)="useDifferentShippingAddress = false"
              class="cursor-pointer">
            <span class="flex-1 cursor-pointer">Gleiche Adresse wie oben angegeben verwenden</span>
          </label>

          <!-- Option 2: Use different address -->
          <label 
            class="radio-button-container"
            [class.selected]="useDifferentShippingAddress"
            (click)="useDifferentShippingAddress = true">
            <input 
              type="radio" 
              name="shipping-address-option" 
              [checked]="useDifferentShippingAddress"
              (change)="useDifferentShippingAddress = true"
              class="cursor-pointer">
            <span class="flex-1 cursor-pointer">Eine andere Adresse verwenden</span>
          </label>

          <!-- Shipping Address Form (shown when "different address" is selected) -->
          @if (useDifferentShippingAddress) {
            <div class="mt-4 p-4 bg-benzGrey-50 rounded-lg border-2 border-benzGrey-300">
              <form [formGroup]="shippingAddressForm" class="flex flex-col gap-3">
              <!-- Vorname -->
              <app-inputerrors [control]="shippingAddressForm.controls.name">
                <label class="flex flex-col gap-1">
                  <span class="text-sm font-medium">Vorname <span class="text-benzRed-700">*</span></span>
                  <input 
                    type="text" 
                    formControlName="name" 
                    placeholder="Max"
                    [class.border-benzRed-700]="shippingAddressForm.controls.name.touched && !shippingAddressForm.controls.name.valid"
                    class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
                </label>
              </app-inputerrors>

              <!-- Nachname -->
              <app-inputerrors [control]="shippingAddressForm.controls.surname">
                <label class="flex flex-col gap-1">
                  <span class="text-sm font-medium">Nachname <span class="text-benzRed-700">*</span></span>
                  <input 
                    type="text" 
                    formControlName="surname" 
                    placeholder="Mustermann"
                    [class.border-benzRed-700]="shippingAddressForm.controls.surname.touched && !shippingAddressForm.controls.surname.valid"
                    class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
                </label>
              </app-inputerrors>

              <!-- Google Address Autocomplete for Shipping -->
              <label class="flex flex-col gap-1">
                <span class="text-sm font-medium">Adresse suchen (zum Autofüllen)</span>
                <address-input (addressSelected)="onShippingAddressAutocomplete($event)"></address-input>
              </label>

              <!-- City -->
              <app-inputerrors [control]="shippingAddressForm.controls.city">
                <label class="flex flex-col gap-1">
                  <span class="text-sm font-medium">Stadt <span class="text-benzRed-700">*</span></span>
                  <input 
                    type="text" 
                    formControlName="city" 
                    placeholder="Stuttgart"
                    [class.border-benzRed-700]="shippingAddressForm.controls.city.touched && !shippingAddressForm.controls.city.valid"
                    class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
                </label>
              </app-inputerrors>

              <!-- Street -->
              <app-inputerrors [control]="shippingAddressForm.controls.street">
                <label class="flex flex-col gap-1">
                  <span class="text-sm font-medium">Straße <span class="text-benzRed-700">*</span></span>
                  <input 
                    type="text" 
                    formControlName="street" 
                    placeholder="Kronprinzstr."
                    [class.border-benzRed-700]="shippingAddressForm.controls.street.touched && !shippingAddressForm.controls.street.valid"
                    class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
                </label>
              </app-inputerrors>

              <!-- Street Number -->
              <app-inputerrors [control]="shippingAddressForm.controls.street_nr">
                <label class="flex flex-col gap-1">
                  <span class="text-sm font-medium">Hausnummer <span class="text-benzRed-700">*</span></span>
                  <input 
                    type="text" 
                    formControlName="street_nr" 
                    placeholder="24"
                    [class.border-benzRed-700]="shippingAddressForm.controls.street_nr.touched && !shippingAddressForm.controls.street_nr.valid"
                    class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
                </label>
              </app-inputerrors>

              <!-- Postal code -->
              <app-inputerrors [control]="shippingAddressForm.controls.zipcode">
                <label class="flex flex-col gap-1">
                  <span class="text-sm font-medium">Postleitzahl <span class="text-benzRed-700">*</span></span>
                  <input 
                    type="text" 
                    formControlName="zipcode" 
                    placeholder="70173"
                    [class.border-benzRed-700]="shippingAddressForm.controls.zipcode.touched && !shippingAddressForm.controls.zipcode.valid"
                    class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
                </label>
              </app-inputerrors>

              <!-- Telefonnummer -->
              <app-inputerrors [control]="shippingAddressForm.controls.phone">
                <label class="flex flex-col gap-1">
                  <span class="text-sm font-medium">Telefonnummer <span class="text-benzRed-700">*</span></span>
                  <input 
                    type="tel" 
                    formControlName="phone" 
                    placeholder="01234 567890"
                    [class.border-benzRed-700]="shippingAddressForm.controls.phone.touched && !shippingAddressForm.controls.phone.valid"
                    class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
                </label>
              </app-inputerrors>
            </form>
          </div>
        }
        </div>
      }
    </div>
  }

  <!-- Additional Comments Section -->
  <div class="flex flex-col gap-3 pb-6 border-b border-benzGrey-200">
    <label for="additional-comment" class="flex flex-col gap-2">
      <span class="text-lg font-medium">Möchten Sie uns noch etwas mitteilen?</span>
      <input 
        id="additional-comment"
        [(ngModel)]="additionalComment"
        type="text" 
        placeholder="(Optional)"
        class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
    </label>

    <label for="referer-select" class="flex flex-col gap-2">
      <span class="text-lg font-medium">Wie sind Sie auf uns aufmerksam geworden?</span>
      <select 
        id="referer-select"
        [(ngModel)]="referer" 
        class="bg-white border-2 border-benzGrey-900 invalid:font-light invalid:text-benzGrey-600 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700">
        <option selected disabled value="">(Optional)</option>
        @for (referrerOption of refererOptions(); track $index) {
          <option value="{{referrerOption.value}}">{{referrerOption.label}}</option>
        }
      </select>
    </label>
  </div>

  <!-- Legal Notice Section -->
  <div class="flex flex-col gap-3 pb-6">
    <h2 class="text-2xl leading-snug font-medium">Rechtlicher Hinweis</h2>
    <div class="overflow-y-scroll bg-white p-4 border-2 border-benzGrey-900 max-h-[25vh] rounded-lg" role="region" aria-label="Preisinformationen" tabindex="0">
      <p class="text-sm text-benzGrey-700">
        Die genannten Preise verstehen sich inklusive 19% MwSt. und Bediengeld (BG*) zzgl. optionaler Leistungen (Versand / Wertmarken / Wertschecks und Zutrittsbänder).
      </p>
    </div>
  </div>

  <!-- Terms and Conditions Section -->
  <div class="flex flex-col gap-3 pb-6">
    <div class="overflow-y-scroll bg-white p-4 border-2 border-benzGrey-900 max-h-[25vh] rounded-lg" role="region" aria-label="Allgemeine Geschäftsbedingungen" tabindex="0">
      <p class="text-sm text-benzGrey-700">
        Es wird darauf hingewiesen, dass nach §312g Abs. 2 Nr. 9 BGB kein Widerrufsrecht besteht.<br/><br/>
        Bitte beachten Sie unsere <a href="https://www.beimbenz.de/agb" target="_blank" class="text-benzRed-700 underline hover:text-benzRed-800 focus:outline-none focus:ring-2 focus:ring-benzRed-700">Allgemeinen Geschäftsbedingungen</a> (AGB) und die <a class="text-benzRed-700 underline hover:text-benzRed-800 focus:outline-none focus:ring-2 focus:ring-benzRed-700" target="_blank" href="https://www.beimbenz.de/hausordnung">Hausordnung</a>.
      </p>
    </div>

    <label 
      class="flex gap-3 items-start cursor-pointer p-3 rounded-lg hover:bg-benzGrey-50 transition-colors"
      [class.text-benzRed-700]="!acceptTermsAndConditions && triedToSubmit">
      <input 
        type="checkbox" 
        id="accept-terms"
        [(ngModel)]="acceptTermsAndConditions"
        aria-required="true"
        [attr.aria-invalid]="!acceptTermsAndConditions && triedToSubmit"
        class="mt-1 border-2 border-benzGrey-900 scale-125 accent-benzRed-700 focus:outline-none focus:ring-2 focus:ring-benzRed-700 cursor-pointer">
      <span class="text-sm">
        Ich erkläre hiermit, dass ich mit den AGB und der Hausordnung einverstanden bin.
      </span>
    </label>

    @if (!acceptTermsAndConditions && triedToSubmit) {
      <p role="alert" class="text-benzRed-700 text-sm mt-1">
        Bitte akzeptieren Sie die AGB und Hausordnung, um fortzufahren.
      </p>
    }
  </div>

  <!-- Submit Button -->
  <button 
    type="button" 
    (click)="confirmBooking()" 
    class="w-full px-8 py-4 bg-benzRed-700 text-white text-lg font-semibold rounded-lg hover:bg-benzRed-800 transition-colors focus:outline-none focus:ring-4 focus:ring-benzRed-300" 
    aria-label="Reservierung kostenpflichtig abschließen">
    Kostenpflichtig reservieren
  </button>
</div>
